import React, { useMemo, useState } from 'react';
import {
  Button,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  createPadachhedGanaLink,
  deletePadachhedGanaLink,
  updatePadachhedGanaLink,
  type PadachhedGanaLink,
} from '../api/ashtadhyayiPadachhedGanaApi';
import type { Gana } from '../api/ganaApi';

type LinkFormValues = {
  ganaId: number;
};

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || 'Request failed';
}

function ganaLabel(g: { id: number; name: string }): string {
  return `${g.name} (#${g.id})`;
}

type PadachhedGanaLinksPanelProps = {
  padachhedId: number;
  padachhedText: string;
  links: PadachhedGanaLink[];
  ganaOptions: Gana[];
  lookupLoading: boolean;
  onChanged: () => Promise<void>;
};

export function PadachhedGanaLinksPanel({
  padachhedId,
  links,
  ganaOptions,
  lookupLoading,
  onChanged,
}: PadachhedGanaLinksPanelProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PadachhedGanaLink | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<LinkFormValues>();

  const ganaSelectOptions = useMemo(
    () =>
      ganaOptions.map((g) => ({
        value: g.id,
        label: ganaLabel(g),
        disabled: links.some((l) => l.ganaId === g.id && l.id !== editing?.id),
      })),
    [ganaOptions, links, editing?.id]
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (link: PadachhedGanaLink) => {
    setEditing(link);
    form.setFieldsValue({ ganaId: link.ganaId });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onDelete = async (id: number) => {
    try {
      await deletePadachhedGanaLink(id);
      message.success('Link deleted');
      await onChanged();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const submitLink = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        if (values.ganaId === editing.ganaId) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updatePadachhedGanaLink(editing.id, { ganaId: values.ganaId });
        message.success('Link updated');
      } else {
        await createPadachhedGanaLink({
          ashtadhyayiPadachhedId: padachhedId,
          ganaId: values.ganaId,
        });
        message.success('Link created');
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const linkColumns: ColumnsType<PadachhedGanaLink> = [
    {
      title: 'Gana',
      key: 'gana',
      render: (_, link) => {
        const g = link.gana ?? ganaOptions.find((x) => x.id === link.ganaId);
        return g ? ganaLabel(g) : `#${link.ganaId}`;
      },
    },
    {
      title: 'Subantas in gana',
      key: 'associations',
      render: (_, link) => {
        const assocs = link.gana?.associations ?? [];
        if (assocs.length === 0) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        return (
          <Space direction="vertical" size={4}>
            {assocs.map((a) => {
              const s = a.subanta;
              const label = s
                ? `${s.word}${s.iast ? ` — ${s.iast}` : ''}${s.linga ? ` (${s.linga})` : ''}`
                : `#${a.subantaId}`;
              return (
                <Typography.Text key={a.subantaId} style={{ fontSize: 12 }}>
                  {label}
                </Typography.Text>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, link) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(link)}>
            Edit
          </Button>
          <Popconfirm title="Delete this link?" onConfirm={() => void onDelete(link.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={openCreate}
          disabled={lookupLoading}
        >
          Add link
        </Button>
      </Space>
      <Table<PadachhedGanaLink>
        rowKey="id"
        size="small"
        pagination={false}
        columns={linkColumns}
        dataSource={links}
        locale={{ emptyText: 'No gana links' }}
      />
      <Modal
        title={editing ? `Edit link #${editing.id}` : 'Add gana link'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submitLink()}
        confirmLoading={saving}
        destroyOnClose
        width={480}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Links the whole gana (all its subanta associations) to this padachhed.
        </Typography.Paragraph>
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="ganaId"
            label="Gana"
            rules={[{ required: true, message: 'Select a gana' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select gana"
              options={ganaSelectOptions}
              loading={lookupLoading}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
